// components/AuthContext.js
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';
import { getApiBase, getAuthServerBase } from '../utils/runtime';

const AuthContext = createContext(null);
const STORAGE_KEY = 'kmq-auth-cache-v2';

function loadCachedUser() {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function persistCachedUser(value) {
  if (typeof window === 'undefined') return;
  if (!value) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(() => loadCachedUser());
  const [loading, setLoading] = useState(true);
  useLanguage();

  const AUTH_SERVER = getAuthServerBase();
  const API_BASE = getApiBase();

  const clearSession = useCallback(() => {
    setAuthUser(null);
    setProfile(null);
    persistCachedUser(null);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const meResponse = await axios.get(`${AUTH_SERVER}/me`, {
        withCredentials: true,
        timeout: 6000,
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      const sessionUser = meResponse?.data?.user || meResponse?.data || null;
      if (!sessionUser?.email) {
        clearSession();
        return null;
      }

      setAuthUser(sessionUser);

      try {
        const profileResponse = await axios.get(`${API_BASE}/users/email/${encodeURIComponent(sessionUser.email)}`, {
          timeout: 6000
        });
        const mergedUser = {
          ...sessionUser,
          ...(profileResponse?.data || {})
        };
        setProfile(mergedUser);
        persistCachedUser(mergedUser);
        return mergedUser;
      } catch {
        const mergedUser = {
          ...sessionUser,
          user_id: sessionUser.user_id || sessionUser.sub || null
        };
        setProfile(mergedUser);
        persistCachedUser(mergedUser);
        return mergedUser;
      }
    } catch {
      clearSession();
      return null;
    }
  }, [API_BASE, AUTH_SERVER, clearSession]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    refreshSession().finally(() => {
      if (mounted) setLoading(false);
    });

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    };

    window.addEventListener('focus', refreshSession);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mounted = false;
      window.removeEventListener('focus', refreshSession);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshSession]);

  const login = useCallback((flow = 'patient', options = {}) => {
    const redirectTo = options.redirectTo || `${window.location.origin}/redirect-after-login`;
    const params = new URLSearchParams({
      flow,
      redirectTo
    });
    if (options.signup) params.set('signup', '1');
    window.location.href = `${AUTH_SERVER}/login?${params.toString()}`;
  }, [AUTH_SERVER]);

  const logout = useCallback(() => {
    clearSession();
    window.location.assign(`${AUTH_SERVER}/logout`);
  }, [AUTH_SERVER, clearSession]);

  const user = useMemo(
    () => (profile ? { ...authUser, ...profile } : authUser),
    [authUser, profile]
  );

  const value = useMemo(() => ({
    user,
    profile,
    authUser,
    setUser: setProfile,
    setProfile,
    logout,
    loading,
    refreshSession,
    loginPatient: (options) => login('patient', options),
    loginProfessional: (options) => login('professional', options),
    signupPatient: (options) => login('patient', { ...(options || {}), signup: true }),
    isAuthenticated: Boolean(user?.email),
    role: user?.user_type || null
  }), [authUser, loading, login, logout, profile, refreshSession, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
